import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Hotspot, IntegrationsElement } from '@recruitee/integrations/types';
import { groupByKey } from '@recruitee/utils';

import { IntegrationsApiService } from './integrations-api.service';
import { hotspotToSignature } from './utils';

export interface IntegrationElement {
  component: string;
  hotspot: Hotspot;
  inputs: {
    [key: string]: any;
  };
}

export interface IntegrationElementsResponse {
  elements: IntegrationElement[];
}

export interface ElementsWithSignature {
  signature: string;
  elements: IntegrationsElement[];
}

export type IntegrationAbilityInfo = { kind: string; abilities: string[] };

@Injectable({ providedIn: 'root' })
export class IntegrationsHttpClient {
  constructor(private integrationsApi: IntegrationsApiService) {}

  public loadElements(hotspots: Hotspot[]): Observable<ElementsWithSignature[]> {
    return this.integrationsApi.post('elements', { hotspots }).pipe(
      catchError(() => of({ elements: [] })),
      map(({ elements }: IntegrationElementsResponse) => {
        const elementsByHotspot = groupByKey(
          elements.map(element => ({
            ...element,
            hotspot: hotspotToSignature(element.hotspot),
          })),
          'hotspot',
        );
        return hotspots.map(hotspot => {
          const signature = hotspotToSignature(hotspot);
          return {
            signature,
            elements: (elementsByHotspot[signature] || []).map(
              element =>
                <IntegrationsElement>{
                  ...element,
                  hotspot,
                },
            ),
          };
        });
      }),
    );
  }

  public loadEnabledIntegrationKinds(): Observable<string[]> {
    return this.integrationsApi.get('enabled-integration-kinds').pipe(map(data => data.kinds));
  }

  public loadIntegrationAbilities(kinds: string[]): Observable<IntegrationAbilityInfo[]> {
    return this.integrationsApi
      .post('integration-abilities', { kinds })
      .pipe(map(data => data.integrationAbilities));
  }

  public loadFeatureFlags(): Observable<Record<string, boolean>> {
    return this.integrationsApi
      .get<{ features: string[] }>('features')
      .pipe(map(({ features }) => Object.fromEntries(features.map(feature => [feature, true]))));
  }
}
